import { useEffect, useState } from "react";

import { Shield, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/components/ui/sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";

interface RateLimitTier {
  tier: string;
  min_reputation: number;
  max_reputation: number;
  ip_checks_per_hour: number;
  user_checks_per_hour: number;
  description: string;
}

interface UserReputation {
  user_id: string;
  reputation_score: number;
  reputation_tier: string;
  total_security_incidents: number;
  total_successful_checks: number;
  last_incident_at: string | null;
  last_successful_check_at: string | null;
}

interface TierDistribution {
  tier: string;
  count: number;
}

interface RecentViolation {
  id: string;
  user_id: string | null;
  ip_address: string | null;
  violation_type: string;
  severity: string;
  reputation_impact: number;
  created_at: string;
  metadata: any;
}

export const RateLimitTiersMonitor = () => {
  const [tiers, setTiers] = useState<RateLimitTier[]>([]);
  const [distribution, setDistribution] = useState<TierDistribution[]>([]);
  const [recentViolations, setRecentViolations] = useState<RecentViolation[]>([]);
  const [topOffenders, setTopOffenders] = useState<UserReputation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
    
    // Refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      // Fetch rate limit tiers
      const { data: tiersData, error: tiersError } = await supabase
        .from("rate_limit_tiers")
        .select("*")
        .order("min_reputation", { ascending: false });

      if (tiersError) throw tiersError;
      setTiers(tiersData || []);

      // Fetch tier distribution
      const { data: distData, error: distError } = await supabase
        .from("user_reputation")
        .select("reputation_tier");

      if (distError) throw distError;

      // Count users in each tier
      const tierCounts: { [key: string]: number } = {};
      distData?.forEach((user) => {
        tierCounts[user.reputation_tier] = (tierCounts[user.reputation_tier] || 0) + 1;
      });

      const distArray = Object.entries(tierCounts).map(([tier, count]) => ({
        tier,
        count,
      }));
      setDistribution(distArray);

      // Fetch recent violations (last 24 hours)
      const { data: violationsData, error: violationsError } = await supabase
        .from("security_violation_history")
        .select("*")
        .gte("created_at", new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString())
        .order("created_at", { ascending: false })
        .limit(20);

      if (violationsError) throw violationsError;
      setRecentViolations(violationsData || []);

      // Fetch top offenders
      const { data: offendersData, error: offendersError } = await supabase
        .from("user_reputation")
        .select("*")
        .order("total_security_incidents", { ascending: false })
        .limit(10);

      if (offendersError) throw offendersError;
      setTopOffenders(offendersData || []);

    } catch (error) {
      console.error("Error fetching rate limit data:", error);
      toast.error("Failed to load rate limit monitoring data");
    } finally {
      setLoading(false);
    }
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case "trusted":
        return "bg-green-500/10 text-green-500 border-green-500/20";
      case "normal":
        return "bg-blue-500/10 text-blue-500 border-blue-500/20";
      case "cautious":
        return "bg-yellow-500/10 text-yellow-500 border-yellow-500/20";
      case "restricted":
        return "bg-orange-500/10 text-orange-500 border-orange-500/20";
      case "severe":
        return "bg-red-500/10 text-red-500 border-red-500/20";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "low":
        return "bg-blue-500/10 text-blue-500";
      case "medium":
        return "bg-yellow-500/10 text-yellow-500";
      case "high":
        return "bg-orange-500/10 text-orange-500";
      case "critical":
        return "bg-red-500/10 text-red-500";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  const getTierIcon = (tier: string) => {
    switch (tier) {
      case "trusted":
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case "normal":
        return <Shield className="h-4 w-4 text-blue-500" />;
      case "cautious":
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case "restricted":
      case "severe":
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      default:
        return <Shield className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-muted-foreground text-center">Loading rate limit monitoring data...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Progressive Rate Limiting Monitor
          </CardTitle>
          <CardDescription>
            Monitor reputation-based rate limits and security violations
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="tiers" className="space-y-4">
            <TabsList>
              <TabsTrigger value="tiers">Rate Limit Tiers</TabsTrigger>
              <TabsTrigger value="distribution">User Distribution</TabsTrigger>
              <TabsTrigger value="violations">Recent Violations</TabsTrigger>
              <TabsTrigger value="offenders">Top Offenders</TabsTrigger>
            </TabsList>

            <TabsContent value="tiers" className="space-y-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tier</TableHead>
                    <TableHead>Reputation Range</TableHead>
                    <TableHead>IP Checks/Hour</TableHead>
                    <TableHead>User Checks/Hour</TableHead>
                    <TableHead>Description</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tiers.map((tier) => (
                    <TableRow key={tier.tier}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getTierIcon(tier.tier)}
                          <Badge variant="outline" className={getTierColor(tier.tier)}>
                            {tier.tier.toUpperCase()}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        {tier.min_reputation} to {tier.max_reputation}
                      </TableCell>
                      <TableCell>{tier.ip_checks_per_hour}</TableCell>
                      <TableCell>{tier.user_checks_per_hour}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {tier.description}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TabsContent>

            <TabsContent value="distribution" className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {distribution.map((item) => {
                  const tier = tiers.find((t) => t.tier === item.tier);
                  return (
                    <Card key={item.tier}>
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-sm font-medium flex items-center gap-2">
                            {getTierIcon(item.tier)}
                            {item.tier.toUpperCase()}
                          </CardTitle>
                          <Badge variant="outline" className={getTierColor(item.tier)}>
                            {item.count} users
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-xs text-muted-foreground">
                          {tier?.description}
                        </p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </TabsContent>

            <TabsContent value="violations" className="space-y-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Time</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Severity</TableHead>
                    <TableHead>Impact</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentViolations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground">
                        No violations in the last 24 hours
                      </TableCell>
                    </TableRow>
                  ) : (
                    recentViolations.map((violation) => (
                      <TableRow key={violation.id}>
                        <TableCell className="text-sm">
                          {new Date(violation.created_at).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {violation.violation_type.replace(/_/g, " ")}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={getSeverityColor(violation.severity)}>
                            {violation.severity}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span
                            className={`flex items-center gap-1 ${
                              violation.reputation_impact < 0
                                ? "text-red-500"
                                : "text-green-500"
                            }`}
                          >
                            {violation.reputation_impact < 0 ? (
                              <TrendingDown className="h-4 w-4" />
                            ) : (
                              <TrendingUp className="h-4 w-4" />
                            )}
                            {violation.reputation_impact}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {violation.ip_address && `IP: ${violation.ip_address.slice(0, 15)}...`}
                          {violation.metadata?.current_tier && ` (${violation.metadata.current_tier})`}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TabsContent>

            <TabsContent value="offenders" className="space-y-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User ID</TableHead>
                    <TableHead>Reputation</TableHead>
                    <TableHead>Tier</TableHead>
                    <TableHead>Incidents</TableHead>
                    <TableHead>Success</TableHead>
                    <TableHead>Last Incident</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topOffenders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground">
                        No user reputation data available
                      </TableCell>
                    </TableRow>
                  ) : (
                    topOffenders.map((user) => (
                      <TableRow key={user.user_id}>
                        <TableCell className="font-mono text-sm">
                          {user.user_id.slice(0, 8)}...
                        </TableCell>
                        <TableCell>
                          <span
                            className={
                              user.reputation_score >= 100
                                ? "text-green-500"
                                : user.reputation_score >= 50
                                ? "text-blue-500"
                                : user.reputation_score >= 0
                                ? "text-yellow-500"
                                : "text-red-500"
                            }
                          >
                            {user.reputation_score}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={getTierColor(user.reputation_tier)}
                          >
                            {user.reputation_tier}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="destructive">
                            {user.total_security_incidents}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-green-500/10 text-green-500">
                            {user.total_successful_checks}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {user.last_incident_at
                            ? new Date(user.last_incident_at).toLocaleString()
                            : "Never"}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};
